import type { MediaSignal, MediaSignalFreshness } from '@tsr/shared-types';

/**
 * News & Social feed helpers (#65/#183 wave 3b) — the pure query/format logic
 * behind the two operator homes (panel "News & Social" tab + COP left-rail
 * tab), kept out of the component for unit testing.
 *
 * EVENT MODE (the spec's raw-firehose toggle, OFF by default): the default
 * view is the lexicon-matched feed narrowed by the operator's filters; event
 * mode is the affordance that drops every narrowing filter and shows the whole
 * wire the API returns (in v1 the wire only stores lexicon-matched rows, so
 * "raw" means un-narrowed, not un-gated). The freshness window and any
 * incident-focus bbox survive event mode — they are navigation context, not
 * curation.
 */

export interface SignalFeedFilters {
  /** Canonical HAZARD_LEXICON key, or 'all'. */
  hazard: string;
  language: 'all' | 'en' | 'fr';
  source: 'all' | 'gdelt' | 'rss' | 'bluesky' | 'x';
  /** The "since" filter — freshness window in hours (API `hours` param). */
  sinceHours: number;
}

export const DEFAULT_SIGNAL_FILTERS: SignalFeedFilters = {
  hazard: 'all',
  language: 'all',
  source: 'all',
  sinceHours: 72,
};

export const SINCE_CHOICES: ReadonlyArray<{ hours: number; label: string }> = [
  { hours: 24, label: 'Last 24 h' },
  { hours: 72, label: 'Last 3 days' },
  { hours: 168, label: 'Last week' },
];

/** A geographic focus handed to the feed (the #183 corroboration chip). */
export interface SignalFeedFocus {
  west: number;
  south: number;
  east: number;
  north: number;
  /** Operator-facing chip copy, e.g. the incident number. */
  label: string;
}

export const SIGNAL_FEED_LIMIT = 200;

/**
 * Build the /api/media-signals query string for the current view. Event mode
 * bypasses hazard/language/source narrowing; focus (bbox) and the freshness
 * window always apply. Dismissed signals are excluded server-side by default —
 * the feed never asks for them back.
 */
export function buildSignalQuery(
  filters: SignalFeedFilters,
  opts: { eventMode?: boolean; focus?: SignalFeedFocus | null; limit?: number } = {},
): string {
  const params = new URLSearchParams();
  params.set('hours', String(filters.sinceHours));
  params.set('limit', String(opts.limit ?? SIGNAL_FEED_LIMIT));
  if (opts.focus) {
    const { west, south, east, north } = opts.focus;
    params.set('bbox', `${west},${south},${east},${north}`);
  }
  if (!opts.eventMode) {
    if (filters.hazard !== 'all') params.set('hazard', filters.hazard);
    if (filters.language !== 'all') params.set('lang', filters.language);
    if (filters.source !== 'all') params.set('source', filters.source);
  }
  return params.toString();
}

export const BULLETIN_FEED_LIMIT = 20;

/**
 * Build the /api/media-signal-bulletins query for the current view (#65 surge
 * bulletins — the LLM-summarized cluster cards atop the feed). Bulletins share
 * the feed's freshness window and any incident-focus bbox, and narrow by the
 * hazard filter; language/source filters don't apply (a bulletin summarizes a
 * whole multi-source cluster). Event mode keeps bulletins — they're additive
 * signal, not curation the firehose should drop.
 */
export function buildBulletinQuery(
  filters: SignalFeedFilters,
  opts: { eventMode?: boolean; focus?: SignalFeedFocus | null; limit?: number } = {},
): string {
  const params = new URLSearchParams();
  params.set('hours', String(filters.sinceHours));
  params.set('limit', String(opts.limit ?? BULLETIN_FEED_LIMIT));
  if (opts.focus) {
    const { west, south, east, north } = opts.focus;
    params.set('bbox', `${west},${south},${east},${north}`);
  }
  if (!opts.eventMode && filters.hazard !== 'all') params.set('hazard', filters.hazard);
  return params.toString();
}

export const CLUSTER_FEED_LIMIT = 30;

/**
 * Build the /api/media-signal-clusters query (#349 surge detector — the count
 * cards that LEAD the social-surge lane). Clusters share the feed's freshness
 * window + any incident-focus bbox and narrow by the hazard filter, exactly like
 * bulletins (language/source don't apply — a cluster spans many sources). Event
 * mode keeps clusters: they are the surge signal, not curation the firehose drops.
 */
export function buildClusterQuery(
  filters: SignalFeedFilters,
  opts: { eventMode?: boolean; focus?: SignalFeedFocus | null; limit?: number } = {},
): string {
  const params = new URLSearchParams();
  params.set('hours', String(filters.sinceHours));
  params.set('limit', String(opts.limit ?? CLUSTER_FEED_LIMIT));
  if (opts.focus) {
    const { west, south, east, north } = opts.focus;
    params.set('bbox', `${west},${south},${east},${north}`);
  }
  if (!opts.eventMode && filters.hazard !== 'all') params.set('hazard', filters.hazard);
  return params.toString();
}

// ── Lane split (#296 social-surge wire + #297 official backstop) ─────────────

/**
 * The surface presents the wire as THREE clearly-labelled lanes (the founder's
 * packaging): the MODERN social-surge lane — X + Bluesky chatter plus the surge
 * bulletins, framed "trending — not confirmed" — then, on the TRADITIONAL /
 * AUTHORITATIVE side, an OFFICIAL ALERTS lane (#297: CCCS / GoC recalls / PHAC,
 * read as official, never "trending") and the HEADLINE WIRE of established GDELT /
 * RSS outlets. The split is computed at read time over the already-fetched
 * signals — no extra request, no server change. An item is OFFICIAL when the API
 * flags it authoritative (attrs.tier='official'); otherwise social → surge, else
 * → headline.
 */
export const SOCIAL_SURGE_SOURCES: ReadonlySet<MediaSignal['source']> = new Set(['x', 'bluesky']);

/** A social source (X / Bluesky) belongs in the MODERN surge lane; GDELT / RSS
 *  are the TRADITIONAL side. */
export function isSurgeSource(source: MediaSignal['source']): boolean {
  return SOCIAL_SURGE_SOURCES.has(source);
}

/** An OFFICIAL authoritative signal (#297) — a curated government/agency feed,
 *  flagged by the API regardless of its source bucket. */
export function isOfficialSignal(signal: Pick<MediaSignal, 'authoritative'>): boolean {
  return signal.authoritative === true;
}

export interface LanedSignals {
  /** MODERN lane — X + Bluesky social chatter (trending, NOT confirmed). */
  surge: MediaSignal[];
  /** AUTHORITATIVE lane (#297) — official government/agency alerts. */
  official: MediaSignal[];
  /** TRADITIONAL lane — GDELT + RSS established headlines. */
  headline: MediaSignal[];
}

/** Partition the fetched wire into its three lanes, preserving order within each.
 *  Official wins over the source split (an authoritative rss feed is OFFICIAL, not
 *  a headline). */
export function partitionSignalsByLane(signals: readonly MediaSignal[]): LanedSignals {
  const surge: MediaSignal[] = [];
  const official: MediaSignal[] = [];
  const headline: MediaSignal[] = [];
  for (const s of signals) {
    if (isOfficialSignal(s)) official.push(s);
    else if (isSurgeSource(s.source)) surge.push(s);
    else headline.push(s);
  }
  return { surge, official, headline };
}

// ── Official-alert strength routing (#373, founder ruling 2026-06-19) ─────────

/**
 * The hazard families that make an OFFICIAL alert strong enough to elevate.
 *
 * The founder ruling collapsed the weak middle-tier "Official alerts" lane: an
 * official item is EITHER strong enough to warrant urgent prominence, OR it folds
 * into the regular news feed alongside RSS + surge bulletins. But a media signal
 * carries NO severity/priority field — the upstream official feeds (CCCS cyber
 * advisories, GoC recalls, PHAC notices) don't publish a machine-readable level,
 * and {@link MediaSignal} deliberately stores none. So the ONLY honest strength
 * signal available client-side is the hazard family: an official alert about an
 * immediate physical life-safety threat (a fire, a flood, an evacuation) is
 * elevated; an authoritative cyber advisory, a routine product recall, or a
 * public-health notice folds into the news feed.
 *
 * This is deliberately CONSERVATIVE (the #370 Urgent-Alerts de-flood ethos):
 * we under-elevate rather than cry wolf, and we never fabricate urgency the data
 * can't support. cyber / public_health / civil_disturbance / power_outage are
 * intentionally absent — distinguishing a pandemic declaration from a cheese
 * recall (both `public_health`) needs a severity field the feed doesn't carry;
 * surfacing every recall as urgent would just rebuild the weak middle tier. The
 * gap (a genuinely critical recall/advisory can't be told apart from a routine
 * one) is a backend follow-up — parse feed/CAP severity into media_signals —
 * NOT something to fake here.
 */
export const URGENT_OFFICIAL_HAZARDS: ReadonlySet<string> = new Set([
  'wildfire',
  'smoke_air_quality',
  'flood',
  'hurricane',
  'tornado',
  'severe_storm',
  'winter_ice_storm',
  'earthquake',
  'landslide',
  'evacuation',
  'extreme_heat',
]);

/** A STRONG official alert (#373): authoritative AND about an immediate physical
 *  life-safety hazard family. Strong alerts are elevated; everything else folds
 *  into the news feed. */
export function isUrgentOfficialSignal(
  signal: Pick<MediaSignal, 'authoritative' | 'hazardKeywords'>,
): boolean {
  return isOfficialSignal(signal) && signal.hazardKeywords.some((k) => URGENT_OFFICIAL_HAZARDS.has(k));
}

export interface RoutedSignals {
  /** STRONG official alerts (#373) — elevated to the urgent band atop the surface. */
  urgentOfficial: MediaSignal[];
  /** MODERN lane — X + Bluesky social chatter (trending, NOT confirmed). */
  surge: MediaSignal[];
  /** TRADITIONAL / headline lane — GDELT + RSS, now INCLUDING the WEAK official
   *  alerts folded in (they keep their OFFICIAL provenance tag where they land). */
  headline: MediaSignal[];
  /** ALL official signals (strong + folded), for hazard-family corroboration —
   *  a folded cyber advisory still corroborates a cyber surge. */
  official: MediaSignal[];
}

/**
 * Route the fetched wire by OFFICIAL strength (#373), replacing the standalone
 * Official lane. One pass, preserving wire order within each bucket: strong
 * official alerts rise to {@link RoutedSignals.urgentOfficial}; weak official
 * alerts fold into the headline feed (interleaved with RSS/GDELT by wire order);
 * social → surge; everything else → headline. `official` collects ALL
 * authoritative items (wherever they render) so corroboration is unaffected.
 */
export function routeSignalsByStrength(signals: readonly MediaSignal[]): RoutedSignals {
  const urgentOfficial: MediaSignal[] = [];
  const surge: MediaSignal[] = [];
  const headline: MediaSignal[] = [];
  const official: MediaSignal[] = [];
  for (const s of signals) {
    if (isOfficialSignal(s)) {
      official.push(s);
      if (isUrgentOfficialSignal(s)) urgentOfficial.push(s);
      else headline.push(s); // weak official folds into the regular news feed
    } else if (isSurgeSource(s.source)) {
      surge.push(s);
    } else {
      headline.push(s);
    }
  }
  return { urgentOfficial, surge, headline, official };
}

// ── Media presence (#555 — the in-card "📸 media" chip) ───────────────────────

/**
 * True when the signal carries a renderable media thumbnail. Drives the in-card
 * "📸 media" chip (#555): the unified feed no longer PROMOTES a media post's
 * ordering — the one time-sorted stream keeps every card in chronological place —
 * but a photo/video attachment stays legible at a glance via the chip. Pure
 * predicate; it never creates an anomaly, scores a Response Level, or reorders a
 * post (the retired #361 credible-media promotion did the ordering; the chip does
 * not). Council PART IX honesty bar intact.
 */
export function hasSignalMedia(signal: Pick<MediaSignal, 'media'>): boolean {
  return !!signal.media && typeof signal.media.url === 'string' && signal.media.url.length > 0;
}

/**
 * The window in which a recent OFFICIAL item corroborates a social surge of the
 * same hazard family (#297, the #183 corroboration idiom). Wide enough that a
 * day-old official advisory still corroborates today's chatter, tight enough that
 * a week-old item doesn't.
 */
export const CORROBORATION_WINDOW_H = 48;

/**
 * The official signals (already in the fetched wire) that CORROBORATE a surge
 * card: same hazard family AND recent (within {@link CORROBORATION_WINDOW_H}).
 * Geo is honestly absent on both the social and official sides of the social-first
 * hazards (X/Bluesky + CCCS/recalls/PHAC carry no point), so the match is
 * hazard + recency — never a guessed coordinate. Pure + read-time: this surfaces a
 * "officials are also reporting" read, it NEVER confirms the specific post and
 * NEVER scores a Response Level (operator-decided, per the council bar).
 */
export function corroboratingOfficialSignals(
  surge: Pick<MediaSignal, 'hazardKeywords'>,
  official: readonly MediaSignal[],
  nowMs: number = Date.now(),
): MediaSignal[] {
  if (surge.hazardKeywords.length === 0) return [];
  const keys = new Set(surge.hazardKeywords);
  const cutoff = nowMs - CORROBORATION_WINDOW_H * 3_600_000;
  return official.filter(
    (o) =>
      o.hazardKeywords.some((k) => keys.has(k)) &&
      new Date(signalTime(o)).getTime() >= cutoff,
  );
}

/** Operator-facing source label (provenance chrome — always visible). */
export function signalSourceLabel(source: MediaSignal['source']): string {
  switch (source) {
    case 'gdelt':
      return 'GDELT';
    case 'rss':
      return 'News (RSS)';
    case 'bluesky':
      return 'Bluesky';
    case 'x':
      return 'X';
  }
}

/** Provenance label for an OFFICIAL card (#297): the source host (e.g.
 *  "cyber.gc.ca", "recalls-rappels.canada.ca") so the operator sees the issuing
 *  authority, not the generic "News (RSS)" bucket. Falls back when there's no URL. */
export function officialSourceLabel(signal: Pick<MediaSignal, 'url'>): string {
  if (signal.url) {
    try {
      return new URL(signal.url).host.replace(/^www\./, '');
    } catch {
      /* fall through */
    }
  }
  return 'Official source';
}

/** Card headline: title, else the body excerpt, else the bare URL host. */
export function signalDisplayTitle(signal: Pick<MediaSignal, 'title' | 'body' | 'url'>): string {
  if (signal.title?.trim()) return signal.title.trim();
  if (signal.body?.trim()) return excerpt(signal.body, 120);
  if (signal.url) {
    try {
      return new URL(signal.url).host;
    } catch {
      return signal.url;
    }
  }
  return '(untitled signal)';
}

/** Body excerpt under the headline — empty when the body just repeats it. */
export function signalBodyExcerpt(
  signal: Pick<MediaSignal, 'title' | 'body'>,
  max = 200,
): string {
  const body = signal.body?.trim() ?? '';
  if (!body) return '';
  if (signal.title?.trim() && body === signal.title.trim()) return '';
  return excerpt(body, max);
}

function excerpt(text: string, max: number): string {
  const t = text.trim().replace(/\s+/g, ' ');
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).trimEnd()}…`;
}

/** The signal's display time — published when the source said, else ingested. */
export function signalTime(signal: Pick<MediaSignal, 'publishedAt' | 'ingestedAt'>): string {
  return signal.publishedAt ?? signal.ingestedAt;
}

// ── Server-driven feed freshness (#563) ──────────────────────────────────────

/** Above this many minutes of server-data age, the feed reads STALE. */
export const FEED_STALE_THRESHOLD_MIN = 10;

export interface FeedFreshnessView {
  /** Age of the newest wire data, in minutes, or null when unknown. */
  ageMinutes: number | null;
  /** True when the server's newest data is older than the stale threshold. */
  isStale: boolean;
  /** HH:MM (local) of the newest wire data, for the "updated HH:MM" stamp. */
  updatedLabel: string | null;
}

function hhmm(ms: number): string {
  const d = new Date(ms);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * Compute the feed's freshness view from the SERVER-authored freshness envelope
 * (#563). The age is skew-free and keeps ticking:
 *   ageAtFetch = serverTime − newestIngestedAt   (server−server: no clock skew)
 *   ageNow     = ageAtFetch + (nowMs − receivedAtMs)   (client-elapsed since fetch)
 * So BOTH failure modes trip the badge honestly: a frozen ingestion (ageAtFetch
 * grows across fetches) AND a dead API (ageNow grows via elapsed time on the
 * last-good envelope). Never keyed on client fetch success.
 */
export function computeFeedFreshness(
  freshness: MediaSignalFreshness | null | undefined,
  receivedAtMs: number,
  nowMs: number,
): FeedFreshnessView {
  if (!freshness?.newestIngestedAt) {
    return { ageMinutes: null, isStale: false, updatedLabel: null };
  }
  const newestMs = new Date(freshness.newestIngestedAt).getTime();
  const serverMs = new Date(freshness.serverTime).getTime();
  if (!Number.isFinite(newestMs) || !Number.isFinite(serverMs)) {
    return { ageMinutes: null, isStale: false, updatedLabel: null };
  }
  const ageAtFetchMs = Math.max(0, serverMs - newestMs);
  const elapsedMs = Math.max(0, nowMs - receivedAtMs);
  const ageMinutes = Math.round((ageAtFetchMs + elapsedMs) / 60_000);
  return {
    ageMinutes,
    isStale: ageMinutes > FEED_STALE_THRESHOLD_MIN,
    updatedLabel: hhmm(newestMs),
  };
}

/** True when a signal is a social-source post (Bluesky / X) — the HARD invariant
 *  the News & Wire tab enforces (no social card ever renders there). */
export function isSocialSignal(signal: Pick<MediaSignal, 'source'>): boolean {
  return isSurgeSource(signal.source);
}
